import gql from 'graphql-tag'
import store from '../index'
import { onLogin, onLogout } from '../../apollo/auth'
import { AUTH_TOKEN_LOCALSTORAGE_KEY } from '../../apollo/vue-apollo'

function parseJwt (token) {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
  }).join(''))
  let payload = JSON.parse(jsonPayload); 
  return payload;
}
  
export const init = async ({ commit, dispatch, state }) => {
  commit('setLoaded', false)
  try {
    await store.$apollo.query({
      query: gql`
        query userLocationAccess {
          userLocationAccess
        }
      `,
      fetchPolicy: 'network-only'
    })
  } catch (error) {
    // User is not in Australia!
    commit('setAccessDenied', true)
  }

  //Get an UUID
  await dispatch('setUUID')
  await dispatch('fetchStoreSettings')

  try {
    const token = localStorage.getItem(AUTH_TOKEN_LOCALSTORAGE_KEY)
    if (token) {
      const accountId = parseJwt(token).id
      commit('setCurrentAccountId', accountId)
      await dispatch('fetchCurrentAccount')
    } else {
      commit('setCurrentAccountId', null)
      commit('setCurrentAccount', null)
    }    
    commit('setLoaded', true)
  } catch (error) {
    if (error.message === 'GraphQL error: You are not authorized.') {
      localStorage.removeItem(AUTH_TOKEN_LOCALSTORAGE_KEY)
      await dispatch('init')
    } else if (error.message === 'Network error: Failed to fetch') {
      commit('setNoNetworkConnection', true)
    }
  }
}

export const setStripeKey = async ({ commit, dispatch, state },uuid) => {
  try {
    const result = await store.$apollo.query({
      query: gql`
        query stripePublishableKey {
          stripePublishableKey
        }
      `,
      context: {
        headers: {
          uuid: uuid
        }
      },
      fetchPolicy: 'network-only'
    })
    commit('setStripePublishableKey', result.data.stripePublishableKey)
  } catch (error) {
    commit('setStripePublishableKey', null)
  }
}

export const setUUID = async ({ commit, dispatch, state },renew) => {
  commit('setLoaded', false)
  try {
    let locallyStoredUUID = localStorage.getItem("UUID");
    if(renew){
      locallyStoredUUID = null
    }
    let uuidData = await store.$apollo.query({
      query: gql`
        query myguest($uuid: String){
          guest(uuid: $uuid){
            id
            uuid,
            activeRetailCartId,
            createdAt,
            updatedAt
          }
        }
      `,
      variables: {
        uuid: locallyStoredUUID
      },
      fetchPolicy: 'network-only'
    })
    let guestData = uuidData.data.guest;
    let uuid = guestData.uuid;
    localStorage.setItem("UUID",uuid)
    commit('setCurrentUUID', uuid)
    commit('setCurrentGuest', guestData)
  } catch (error) {
    console.log(error)
  }
  commit('setLoaded', true)
}

export const unsetUUID = async ({ commit, dispatch, state }) => {
  try {
    localStorage.removeItem("UUID");
    commit('setCurrentUUID', null)
    commit('setCurrentGuest', null)
  } catch (error) {
    console.log(error)
  }
}

export const logout = async ({ commit, dispatch, state }) => {
  commit('setCurrentAccountId', null)
  commit('setCurrentAccount', null)
  await dispatch('setUUID',true)
  await onLogout(store.$apollo)
}

export const waitUntilLoaded = async ({ state }) => {
  while (true) {
    if (state.loaded) break
    if (state.noNetworkConnection) break
    await new Promise(resolve => setTimeout(resolve, 10))
  }
}

export const login = async ({ commit, dispatch, state }, { email, password, cartID }) => {
  const result = await store.$apollo.mutate({
    mutation: gql`
      mutation login($email: String!, $password: String!, $cartID: String) {
        login(email: $email, password: $password, cartID: $cartID)
      }
    `,
    variables: {
      email,
      password,
      cartID
    }
  })
  const token = result.data.login
  await onLogin(store.$apollo, token)
  await dispatch('init')
}

export const fetchCurrentAccount = async ({ commit, state }) => {
  const result = await store.$apollo.query({
    query: gql`
      query fetchCurrentAccount ($id: ID!) {
        account(id: $id) {
          id,
          email,
          isAdmin,
          roles,
          activeCartId,
          activeContainerCartId,
          activeRetailCartId,
          ABNACN,
          companyName,
          contactName,
          contactPhone,
          contactPosition,
          shippingAddress,
          addresses {
            address,
            address2,
            city,
            company,
            contactName,
            contactLastname,
            country,
            info,
            phoneNumber,
            postCode,
            shippingNotes,
            state,
          },
          createdAt,
          updatedAt,
          verifiedAt,
          colourFavourites
        }
      }
    `,
    variables: {
      id: state.currentAccountId
    },
    fetchPolicy: 'network-only'
  })  
  commit('setCurrentAccount', result.data.account)
}

export const fetchStoreSettings = async ({ commit, state }) => {
  const result = await store.$apollo.query({
    query: gql`
      query retailStoreSettings {
        retailStoreSettings {
          stockStoreDiscount
        }
      }
    `,
    fetchPolicy: 'network-only'
  }) 
  commit('setStoreSettings', result.data.retailStoreSettings)
}




export const updateCurrentAccount = async ({ commit, state }, input) => {
  const currentAccountBackup = Object.assign({}, state.currentAccount)
  // Optimistic load the changes into the frontend
  commit('updateCurrentAccount', input)
  try {
    const result = await store.$apollo.mutate({
      mutation: gql`
        mutation editAccount($id: ID!, $input: AccountInput!) {
          editAccount(id: $id, input: $input) {
            id,
            email,
            isAdmin,
            roles,
            activeCartId,
            activeContainerCartId,
            activeRetailCartId,
            ABNACN,
            companyName,
            contactName,
            contactPhone,
            contactPosition,
            shippingAddress,
            addresses {
              address,
              address2,
              city,
              company,
              contactName,
              contactLastname,
              country,
              info,
              phoneNumber,
              postCode,
              shippingNotes,
              state,
            },
            createdAt,
            updatedAt,
            verifiedAt,
            colourFavourites
          }
        }
      `,
      variables: {
        id: state.currentAccountId,
        input
      }
    })
    // Update the optimistic data with the real data returned from the server when we get it
    commit('setCurrentAccount', result.data.editAccount)
  } catch (error) {
    // If the graphql mutation failed then revert to the original data
    commit('setCurrentAccount', currentAccountBackup)
    throw error
  }
}

export const updateCurrentGuest = async ({ commit, state }, input) => {
  commit('updateCurrentGuest', input);  
  try {
    const result = await store.$apollo.mutate({
      mutation: gql`
        mutation editGuest($uuid: String!, $input: GuestInput!) {
          editGuest(uuid: $uuid, input: $input) {
            id
            uuid,
            activeRetailCartId,
            createdAt,
            updatedAt
          }
        }
      `,
      variables: {
        uuid: state.currentUUID,
        input
      }
    })
    commit('setCurrentGuest', result.data.editGuest)
  } catch (error) {  
    // If the graphql mutation failed then revert to the original data
    throw error
  }
}